/**
 * What the device does around a running workout: the rest alarm, the screen
 * staying on, and the buzz when a rest ends in the foreground.
 *
 * `createRestAlert` is a controller fed with every new session (the runtime
 * subscribes it to the store); it compares with the last one it saw and acts
 * on the DIFFERENCE, so it is idempotent under re-renders and re-mounts. The
 * native calls are injected: in the app they are `src/native`'s, in the Node
 * tests fakes that record what was asked.
 *
 * ── ONE NOTIFICATION ID ────────────────────────────────────────────────────
 * `REST_NOTIFICATION_ID` is a constant. Scheduling again under it REPLACES
 * the pending alarm (a new set, ±15 s); cancelling it reaches the one that is
 * pending. A per-rest id would leave a skipped rest's alarm to fire later.
 *
 * ── WHEN IT IS CANCELLED ───────────────────────────────────────────────────
 * Whenever the rest stops being the one the alarm was set for: a skip, a set
 * logged from the rest, the end of the session, the session forgotten.
 * Unmounting the runtime does NOT cancel: the alarm exists for the app being
 * in the background, which is exactly when it may be torn down.
 *
 * **And NOT when the rest simply ran out with the app in the background.**
 * That is the one moment the notification exists for, and the phase moving on
 * looks identical to a skip from `update()`'s side — so `arm()`'s own timer
 * says which it was. In the FOREGROUND the cancel stays: the buzz has already
 * been felt and a notification for a rest the person watched end is noise.
 *
 * ── TIME ───────────────────────────────────────────────────────────────────
 * The rest end is `restEndsAt` from the store, an instant; the foreground
 * timer waits `restEndsAt - now()`, where `now` is injected.
 */

import type { KeyValueStorage } from "@repo/client/i18n";
import type {
  ImpactWeight,
  NotificationRequest,
  PermissionState,
  ScheduleOutcome,
} from "../../../native";
import type { WorkoutSession } from "./session-model";

/** Stable, a 32-bit integer (Android). Never derive it from the session. */
export const REST_NOTIFICATION_ID = 53_001;

export interface RestAlertTimers {
  clearTimeout(handle: unknown): void;
  setTimeout(callback: () => void, ms: number): unknown;
}

export interface RestAlertDeps {
  readonly allowSleep: () => Promise<unknown>;
  readonly cancelNotification: (id: number) => Promise<unknown>;
  /**
   * The countdown the system draws while the rest runs, with its buttons
   * (`D283`, `native/rest-timer.ts`). Shown each time a rest is armed,
   * hidden whenever the rest stops being one — including when it simply ran
   * out, unlike the end alarm. Absent (the web, an older shell) is none.
   */
  readonly countdown?: {
    hide(): Promise<unknown>;
    show(endsAt: number): Promise<unknown>;
  };
  readonly impact: (weight: ImpactWeight) => Promise<unknown>;
  /** Whether the app is in front; the buzz is for a person looking at it. */
  readonly isForeground: () => boolean;
  readonly keepAwake: () => Promise<unknown>;
  /**
   * Whether the settings keep the screen on during a workout (`D209`, B11).
   * Absent is on — the behaviour before the setting existed. Read at every
   * `update()`, so turning it off mid-workout lets the screen sleep at the
   * next change of the session; the runtime also calls `update()` when the
   * setting itself changes.
   */
  readonly keepAwakeEnabled?: () => boolean;
  readonly now: () => number;
  /** The store's `finishRest()`. */
  readonly onRestElapsed: () => void;
  /** Told of each scheduling outcome, e.g. to surface inexact alarms once. */
  readonly onScheduled?: (outcome: ScheduleOutcome) => void;
  /**
   * The rest-over sound, in the foreground only (`D209`, B8) — the
   * notification carries the system's sound in the background. Absent plays
   * nothing, as before.
   */
  readonly playSound?: () => void;
  readonly scheduleNotification: (
    request: NotificationRequest
  ) => Promise<ScheduleOutcome>;
  /** The notification's words, in the current locale. `channelName` is the
   *  name Android shows for the rest channel in its own settings list — it is
   *  created on the LOCAL path, never by the push opt-in (`notifications.ts`). */
  readonly texts: () => {
    /** The notification's two buttons (`D209`, B9); absent is none. */
    readonly actions?: { readonly complete: string; readonly skip: string };
    readonly body: string;
    readonly channelName?: string;
    readonly title: string;
  };
  readonly timers?: RestAlertTimers;
  /** Whether the settings vibrate at the end of a rest (B8); absent is on. */
  readonly vibrate?: () => boolean;
}

export interface RestAlert {
  /** Tear down timers and keep-awake. The pending alarm is left in place. */
  stop(): void;
  update(session: WorkoutSession | null): void;
}

const realTimers: RestAlertTimers = {
  clearTimeout: (handle) =>
    globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
};

/** The rest the alarm should be set for, or `null`. */
export function restAlarmAt(session: WorkoutSession | null): number | null {
  if (
    session === null ||
    session.ended !== null ||
    session.phase !== "rest" ||
    session.restEndsAt === null
  ) {
    return null;
  }
  return session.restEndsAt;
}

export function createRestAlert(deps: RestAlertDeps): RestAlert {
  const timers = deps.timers ?? realTimers;
  let alarmAt: number | null = null;
  let awake = false;
  /**
   * The pending notification reached its moment with nobody looking, so it is
   * the only word the person gets and `update()` must leave it alone. See
   * "WHEN IT IS CANCELLED" above.
   */
  let delivered = false;
  let timer: unknown = null;

  const clearTimer = () => {
    if (timer !== null) {
      timers.clearTimeout(timer);
      timer = null;
    }
  };

  const setAwake = (want: boolean) => {
    if (want === awake) {
      return;
    }
    awake = want;
    (want ? deps.keepAwake() : deps.allowSleep()).catch(() => undefined);
  };

  const arm = (at: number) => {
    clearTimer();
    delivered = false;
    const delay = at - deps.now();
    if (delay <= 0) {
      // It landed while nothing was watching (the app was killed): the
      // notification has already fired, so only the phase moves on.
      deps.countdown?.hide().catch(() => undefined);
      deps.onRestElapsed();
      return;
    }
    const { actions, body, channelName, title } = deps.texts();
    deps
      .scheduleNotification({
        ...(actions === undefined ? {} : { actions }),
        at: new Date(at),
        body,
        channelName,
        id: REST_NOTIFICATION_ID,
        title,
      })
      .then((outcome) => deps.onScheduled?.(outcome))
      .catch(() => undefined);
    deps.countdown?.show(at).catch(() => undefined);
    timer = timers.setTimeout(() => {
      timer = null;
      if (deps.isForeground()) {
        if (deps.vibrate?.() ?? true) {
          deps.impact("heavy").catch(() => undefined);
        }
        deps.playSound?.();
      } else {
        // The page keeps running in the background (Capacitor does not pause
        // the WebView's timers), so this lands at the same instant as the
        // alarm — and the phase it moves on to is `restAlarmAt === null`,
        // which used to CANCEL the notification a millisecond before the
        // publisher posted it. Nothing was shown, nothing was logged, and the
        // one moment the alarm exists for was the one it never reached.
        // Observed on the container on 2026-09-21: `LocalNotifications.cancel`
        // at 18:57:57.317 against an alarm due at 18:57:57.
        delivered = true;
      }
      deps.onRestElapsed();
    }, delay);
  };

  return {
    stop() {
      clearTimer();
      setAwake(false);
    },
    update(session) {
      setAwake(
        session !== null &&
          session.ended === null &&
          (deps.keepAwakeEnabled?.() ?? true)
      );
      const next = restAlarmAt(session);
      if (next === alarmAt) {
        return;
      }
      const hadAlarm = alarmAt !== null;
      alarmAt = next;
      if (next === null) {
        clearTimer();
        if (hadAlarm) {
          // Always: a countdown left behind past its rest would read as a
          // rest that never ends (the system removes it at the end on API
          // 26+ only).
          deps.countdown?.hide().catch(() => undefined);
        }
        if (hadAlarm && !delivered) {
          deps.cancelNotification(REST_NOTIFICATION_ID).catch(() => undefined);
        }
        delivered = false;
        return;
      }
      arm(next);
    },
  };
}

// ── Exact alarms, surfaced once ──────────────────────────────────────────────

export interface ExactAlarmNoticeDeps {
  readonly exactAlarmState: () => Promise<PermissionState>;
  /** Where "already told" is kept, so it is said once per install. */
  readonly storage: Pick<KeyValueStorage, "getItem" | "setItem">;
}

export const EXACT_ALARM_NOTICE_KEY = "mypocketgym.workout.exact-alarm-noticed";

/**
 * Whether to show the "the rest alarm may be late" notice now: the last
 * schedule was inexact, exact alarms are not granted (Android 12+ can switch
 * them off), and the person has not been told before. Without the grant the
 * alarm can land minutes late under Doze (`notifications.ts`).
 */
export async function shouldNoticeExactAlarms(
  outcome: ScheduleOutcome,
  deps: ExactAlarmNoticeDeps
): Promise<boolean> {
  if (!outcome.scheduled || outcome.exact) {
    return false;
  }
  const state = await deps.exactAlarmState();
  if (state !== "denied" && state !== "prompt") {
    return false;
  }
  try {
    return (await deps.storage.getItem(EXACT_ALARM_NOTICE_KEY)) === null;
  } catch {
    return false;
  }
}

export async function markExactAlarmsNoticed(
  deps: Pick<ExactAlarmNoticeDeps, "storage">
): Promise<void> {
  try {
    await deps.storage.setItem(EXACT_ALARM_NOTICE_KEY, "1");
  } catch {
    // Not remembered: the notice may show once more next launch.
  }
}
